/**
 * OathUI - Centralized UI Components for Oath Flow
 * Provides consistent styling across all Oath pages
 * Based on Login page design patterns with gradient backgrounds and accent bars
 *
 * Dark Mode Support:
 * - All components support dark mode via Tailwind's dark: prefix
 * - Enable dark mode by adding 'dark' class to <html> or a parent element
 * - Uses 'class' strategy in tailwind.config.js: darkMode: 'class'
 */

import { useState, useMemo, useRef, useEffect } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';

// =============================================================================
// LAYOUT COMPONENTS
// =============================================================================

/**
 * Main page wrapper with branded gradient background (matches login page teal panel)
 * Usage: <OathPageWrapper>...content...</OathPageWrapper>
 * @param {string} maxWidth - Tailwind max-width class (default: 'max-w-5xl')
 * @param {boolean} responsive - If true, automatically scales max-width for QHD/4K screens
 */
export const OathPageWrapper = ({ children, className = '', maxWidth = 'max-w-5xl', fillHeight = true, responsive = false }) => {
  // Build responsive max-width classes if responsive mode is enabled
  const maxWidthClass = responsive
    ? `${maxWidth} qhd:max-w-[2200px] 4k:max-w-[3400px]`
    : maxWidth;

  return (
    <div className={`${fillHeight ? 'min-h-[calc(100vh-48px)] sm:min-h-[calc(100vh-57px)]' : ''} py-4 sm:py-6 relative overflow-hidden ${className}`}>
      {/* Light gray gradient background for better card contrast */}
      <div className="absolute inset-0 bg-gradient-to-br from-gray-100 via-gray-200 to-gray-300 dark:from-gray-900 dark:via-gray-900 dark:to-gray-950" />

      {/* Content */}
      <div className={`relative ${maxWidthClass} mx-auto px-4 sm:px-6 qhd:px-8 4k:px-12`}>
        {children}
      </div>

    </div>
  );
};

/**
 * Header card with title, subtitle, icon, and accent bar (matching login card style)
 * Usage: <OathHeaderCard title="Page Title" subtitle="Description" icon={UsersIcon} />
 */
export const OathHeaderCard = ({
  title,
  subtitle,
  icon: Icon,
  rightContent,
  children,
  compact = false,
  className = ''
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-xl ${compact ? 'mb-4' : 'mb-6'} ${className}`}>
    {/* Orange accent bar like login page - overflow-hidden only on this element to preserve rounded corners */}
    <div className="h-1.5 bg-gradient-to-r from-orange-400 via-orange-500 to-orange-400 rounded-t-2xl" />
    <div className={compact ? 'p-3 sm:p-4' : 'p-5 sm:p-6'}>
      <div className={`flex flex-col sm:flex-row sm:items-center sm:justify-between ${compact ? 'gap-3' : 'gap-4'}`}>
        <div className={`flex items-center ${compact ? 'gap-3' : 'gap-4'}`}>
          {Icon && (
            <div className={`${compact ? 'w-9 h-9' : 'w-12 h-12'} bg-gradient-to-br from-primary to-primary-dark rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-primary/30`}>
              <Icon className={`${compact ? 'h-5 w-5' : 'h-6 w-6'} text-white`} />
            </div>
          )}
          <div>
            <h1 className={`${compact ? 'text-lg' : 'text-xl sm:text-2xl'} font-bold text-gray-900 dark:text-white`}>
              {title}
            </h1>
            {subtitle && (
              <p className={`${compact ? 'text-xs' : 'text-sm'} text-gray-500 dark:text-gray-400 mt-0.5`}>
                {subtitle}
              </p>
            )}
          </div>
        </div>
        {rightContent}
      </div>
      {children && (
        <div className={compact ? 'mt-3' : 'mt-4'}>
          {children}
        </div>
      )}
    </div>
  </div>
);

/**
 * Content card wrapper with accent bar option
 * Usage: <OathContentCard accent>...content...</OathContentCard>
 */
export const OathContentCard = ({
  children,
  className = '',
  noPadding = false,
  accent = false // adds orange accent bar at top
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden ${className}`}>
    {accent && (
      <div className="h-1 bg-gradient-to-r from-primary via-primary to-orange-500" />
    )}
    {noPadding ? children : (
      <div className="p-5 sm:p-6">
        {children}
      </div>
    )}
  </div>
);

/**
 * Section within a card
 * Usage: <OathSection title="Section Title">...content...</OathSection>
 */
export const OathSection = ({ title, children, className = '' }) => (
  <div className={`mb-6 last:mb-0 ${className}`}>
    {title && (
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{title}</h2>
    )}
    {children}
  </div>
);

/**
 * Section card with icon header and accent bar (used in detail pages)
 * Usage: <OathSectionCard title="Section Title" icon={UserIcon}>...content...</OathSectionCard>
 */
export const OathSectionCard = ({
  title,
  icon: Icon,
  children,
  actions,
  className = ''
}) => (
  <div className={`bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-primary-100 dark:border-gray-700 overflow-hidden ${className}`}>
    <div className="px-4 py-3 bg-primary-50/50 dark:bg-gray-700/50 border-b border-primary-100 dark:border-gray-700 flex items-center justify-between">
      <div className="flex items-center gap-2">
        {Icon && <Icon className="h-5 w-5 text-primary dark:text-primary-light" />}
        <h3 className="font-semibold text-gray-900 dark:text-white">{title}</h3>
      </div>
      {actions}
    </div>
    <div className="p-4">{children}</div>
  </div>
);

/**
 * Detail field for label/value display (used in detail pages)
 * Usage: <OathDetailField label="Name" value="John Doe" highlight />
 */
export const OathDetailField = ({ label, value, highlight = false }) => (
  <div className="py-2">
    <dt className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</dt>
    <dd className={`mt-1 text-sm font-medium ${
      highlight
        ? 'text-primary dark:text-primary-light bg-primary-50 dark:bg-primary-900/30 px-2 py-1 rounded'
        : 'text-gray-900 dark:text-white'
    }`}>
      {value || '-'}
    </dd>
  </div>
);

/**
 * Feature card like the login page feature highlights
 * Usage: <OathFeatureCard icon={Icon} title="Feature" description="Description" variant="primary" />
 */
export const OathFeatureCard = ({
  icon: Icon,
  title,
  description,
  onClick,
  variant = 'primary', // 'primary' | 'orange'
  className = ''
}) => {
  const variantClasses = {
    primary: {
      card: 'bg-primary-50/50 dark:bg-primary-900/20 border-primary-100 dark:border-primary-800 hover:bg-primary-50 dark:hover:bg-primary-900/30 hover:border-primary-200 dark:hover:border-primary-700',
      icon: 'bg-gradient-to-br from-primary to-primary-dark shadow-lg shadow-primary/30',
    },
    orange: {
      card: 'bg-orange-50/50 dark:bg-orange-900/20 border-orange-100 dark:border-orange-800 hover:bg-orange-50 dark:hover:bg-orange-900/30 hover:border-orange-200 dark:hover:border-orange-700',
      icon: 'bg-gradient-to-br from-orange-400 to-orange-600 shadow-lg shadow-orange-500/30',
    },
  };

  const styles = variantClasses[variant] || variantClasses.primary;

  const CardContent = (
    <>
      <div className={`w-10 h-10 ${styles.icon} rounded-lg flex items-center justify-center flex-shrink-0`}>
        {Icon && <Icon className="w-5 h-5 text-white" />}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-gray-900 dark:text-white font-medium text-sm">{title}</p>
        {description && <p className="text-gray-500 dark:text-gray-400 text-xs">{description}</p>}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        onClick={onClick}
        className={`w-full flex items-center gap-4 p-4 rounded-xl border transition-all duration-200 text-left ${styles.card} ${className}`}
      >
        {CardContent}
      </button>
    );
  }

  return (
    <div className={`flex items-center gap-4 p-4 rounded-xl border ${styles.card} ${className}`}>
      {CardContent}
    </div>
  );
};

// =============================================================================
// AGENCY COMPONENTS
// =============================================================================

/**
 * Agency selector dropdown for multi-agency users
 * Usage: <OathAgencySelector agencies={[]} selectedAgencyId={id} onSelect={fn} />
 */
export const OathAgencySelector = ({
  agencies = [],
  selectedAgencyId,
  onSelect,
  onChange,
  className = '',
  fullWidth = false
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [measuredWidth, setMeasuredWidth] = useState(null);
  const buttonRef = useRef(null);
  const measureRef = useRef(null);
  const selectedAgency = agencies.find((a) => a.agencyId === selectedAgencyId);
  const handleSelect = onSelect || onChange;
  // Check if full width mode - either via prop or className
  const isFullWidth = fullWidth || className.includes('w-full');

  // Helper to get display label for an agency (supports custom label override)
  const getAgencyLabel = (agency) => agency.label || `${agency.dbaAgencyName} (${agency.agencyId})`;

  // Find the longest agency name to size the dropdown
  const longestLabel = useMemo(() => {
    if (agencies.length === 0) return 'Select Agency';
    return agencies.reduce((longest, agency) => {
      const lbl = getAgencyLabel(agency);
      return lbl.length > longest.length ? lbl : longest;
    }, '');
  }, [agencies]); // eslint-disable-line react-hooks/exhaustive-deps

  // Measure the width of the longest label on mount
  useEffect(() => {
    if (measureRef.current) {
      setMeasuredWidth(measureRef.current.offsetWidth);
    }
  }, [longestLabel]);

  // Calculate dropdown position relative to the button
  const calcPosition = () => {
    if (!buttonRef.current) return {};
    const rect = buttonRef.current.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const dropdownMaxHeight = 240; // max-h-60 = 15rem = 240px
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;
    const gap = 4;
    const horizontalPadding = 16; // Keep 16px from edges on mobile

    // Calculate width - use button width but cap to viewport with padding
    const maxWidth = viewportWidth - (horizontalPadding * 2);
    const dropdownWidth = Math.min(rect.width, maxWidth);

    // Calculate left position - ensure dropdown stays within viewport
    let left = rect.left;
    if (left + dropdownWidth > viewportWidth - horizontalPadding) {
      left = viewportWidth - horizontalPadding - dropdownWidth;
    }
    if (left < horizontalPadding) {
      left = horizontalPadding;
    }

    // Open upward if not enough space below but enough above
    const openUpward = spaceBelow < dropdownMaxHeight + gap && spaceAbove > spaceBelow;

    if (openUpward) {
      return {
        position: 'fixed',
        bottom: viewportHeight - rect.top + gap,
        left: left,
        width: dropdownWidth,
        maxHeight: Math.min(dropdownMaxHeight, spaceAbove - gap),
      };
    }
    return {
      position: 'fixed',
      top: rect.bottom + gap,
      left: left,
      width: dropdownWidth,
      maxHeight: Math.min(dropdownMaxHeight, spaceBelow - gap),
    };
  };

  const handleToggle = () => {
    if (!dropdownOpen) {
      // Calculate position BEFORE opening so the first render is already correct
      setDropdownStyle(calcPosition());
    }
    setDropdownOpen(!dropdownOpen);
  };

  if (agencies.length <= 1) return null;

  return (
    <div className={`relative inline-block ${className}`}>
      {/* Hidden element to measure the longest text */}
      <span
        ref={measureRef}
        className="invisible absolute whitespace-nowrap px-3 py-1.5 text-sm pointer-events-none"
        aria-hidden="true"
      >
        {longestLabel}
        <ChevronDownIcon className="inline h-4 w-4 ml-2" />
      </span>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        className={`flex items-center justify-between px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-700 dark:text-gray-200 hover:border-gray-400 dark:hover:border-gray-500 focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary ${isFullWidth ? 'w-full' : ''}`}
        style={!isFullWidth && measuredWidth ? { minWidth: `${measuredWidth}px`, maxWidth: '100%' } : undefined}
      >
        <span className="truncate">
          {selectedAgency ? getAgencyLabel(selectedAgency) : 'Select Agency'}
        </span>
        <ChevronDownIcon className="h-4 w-4 text-gray-400 ml-2 flex-shrink-0" />
      </button>

      {dropdownOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setDropdownOpen(false)}
          />
          <div
            className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-md shadow-lg z-50 overflow-auto"
            style={dropdownStyle}
          >
            {agencies.map((agency) => (
              <button
                key={agency.agencyId}
                onClick={() => {
                  handleSelect(agency.agencyId);
                  setDropdownOpen(false);
                }}
                className={`w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                  agency.agencyId === selectedAgencyId
                    ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-dark dark:text-primary-light font-medium'
                    : 'text-gray-700 dark:text-gray-200'
                }`}
              >
                {getAgencyLabel(agency)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

/**
 * Agency info display for single-agency users
 * Usage: <OathAgencyInfo agency={selectedAgency} />
 */
export const OathAgencyInfo = ({ agency, className = '' }) => {
  if (!agency) return null;

  return (
    <div className={`mt-4 pt-4 border-t border-gray-200 dark:border-gray-700 ${className}`}>
      <div className="flex items-center gap-3">
        <div className="flex-shrink-0 w-10 h-10 bg-primary/10 dark:bg-primary/20 rounded-full flex items-center justify-center">
          <span className="text-primary dark:text-primary-light font-semibold text-sm">
            {agency.dbaAgencyName?.charAt(0)?.toUpperCase() || 'A'}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
            {agency.dbaAgencyName}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Agency ID: {agency.agencyId}
          </p>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// BUTTON COMPONENTS
// =============================================================================

/**
 * Primary button (matching login page button style)
 * Usage: <OathButton onClick={fn}>Click Me</OathButton>
 */
export const OathButton = ({
  children,
  onClick,
  disabled = false,
  loading = false,
  variant = 'primary', // 'primary' | 'secondary' | 'outline' | 'danger'
  size = 'md', // 'sm' | 'md' | 'lg'
  icon: Icon,
  className = '',
  type = 'button'
}) => {
  const baseClasses = 'inline-flex items-center justify-center gap-2 font-semibold rounded-xl transition-all duration-200 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed';

  const variantClasses = {
    primary: 'bg-primary hover:bg-primary-dark text-white shadow-lg shadow-primary/30 hover:shadow-xl hover:-translate-y-0.5',
    secondary: 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30 hover:shadow-xl hover:-translate-y-0.5',
    success: 'bg-green-600 hover:bg-green-700 text-white shadow-lg shadow-green-600/30 hover:shadow-xl hover:-translate-y-0.5',
    warning: 'bg-yellow-500 hover:bg-yellow-600 text-white shadow-lg shadow-yellow-500/30 hover:shadow-xl hover:-translate-y-0.5',
    outline: 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-400 dark:hover:border-gray-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-600/30 hover:shadow-xl hover:-translate-y-0.5',
  };

  const sizeClasses = {
    sm: 'px-3 py-2.5 sm:py-2 text-xs',
    md: 'px-5 py-2.5 text-sm',
    lg: 'px-6 py-3.5 text-base',
  };

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || loading}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
    >
      {loading ? (
        <OathSpinner size="sm" />
      ) : Icon ? (
        <Icon className="h-4 w-4" />
      ) : null}
      {children}
    </button>
  );
};

// =============================================================================
// FORM COMPONENTS
// =============================================================================

/**
 * Input field (matching login page input style)
 * Usage: <OathInput label="Name" value={val} onChange={fn} required />
 */
export const OathInput = ({
  label,
  type = 'text',
  value,
  onChange,
  placeholder,
  disabled = false,
  error,
  required = false,
  prefix,
  suffix,
  helperText,
  autoComplete = 'off',
  maxLength,
  max,
  min,
  className = ''
}) => (
  <div className={className}>
    {label && (
      <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
    )}
    <div className="relative">
      {prefix && (
        <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
          <span className="text-gray-500 dark:text-gray-400">{prefix}</span>
        </div>
      )}
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete={autoComplete}
        maxLength={maxLength}
        max={max}
        min={min}
        data-1p-ignore
        data-lpignore="true"
        className={`w-full ${prefix ? 'pl-8' : 'px-4'} ${suffix ? 'pr-14' : ''} py-3.5 border rounded-xl text-base sm:text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed transition-all duration-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:dark:invert ${
          error ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
        }`}
      />
      {suffix && (
        <div className="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none">
          <span className="text-gray-400 dark:text-gray-500 text-sm">{suffix}</span>
        </div>
      )}
    </div>
    {error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}
    {helperText && !error && <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">{helperText}</p>}
  </div>
);

/**
 * Select dropdown with custom styled menu (matches OathAgencySelector look)
 * Usage: <OathSelect label="State" value={val} onChange={fn} options={[{value, label}]} />
 */
export const OathSelect = ({
  label,
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  disabled = false,
  loading = false,
  error,
  required = false,
  className = ''
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const buttonRef = useRef(null);
  const searchInputRef = useRef(null);

  const selectedOption = options.find((opt) => opt.value === value);
  const displayText = selectedOption?.label || placeholder;

  const filteredOptions = searchTerm
    ? options.filter((opt) => opt.label.toLowerCase().startsWith(searchTerm.toLowerCase()))
    : options;

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 0);
    }
    if (!isOpen) setSearchTerm('');
  }, [isOpen]);

  // Calculate dropdown position before opening — open upward if not enough space below
  const toggleOpen = () => {
    if (disabled || loading) return;
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;
      const dropdownMaxHeight = 240; // max-h-60 = 15rem = 240px
      const gap = 4;
      const spaceBelow = viewportHeight - rect.bottom;
      const spaceAbove = rect.top;
      const openUpward = spaceBelow < dropdownMaxHeight + gap && spaceAbove > spaceBelow;

      if (openUpward) {
        setDropdownStyle({
          position: 'fixed',
          bottom: viewportHeight - rect.top + gap,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(dropdownMaxHeight, spaceAbove - gap),
        });
      } else {
        setDropdownStyle({
          position: 'fixed',
          top: rect.bottom + gap,
          left: rect.left,
          width: rect.width,
          maxHeight: Math.min(dropdownMaxHeight, spaceBelow - gap),
        });
      }
    }
    setIsOpen(!isOpen);
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
  };

  // Open dropdown and seed search when user types on the focused button
  const handleButtonKeyDown = (e) => {
    if (disabled || loading) return;
    // Ignore modifier keys, tab, escape, enter/space (those toggle normally)
    if (e.key.length !== 1 || e.ctrlKey || e.metaKey || e.altKey) return;
    if (!isOpen) {
      // Open the dropdown first, then seed the search character
      e.preventDefault();
      toggleOpen();
      // Delay setting the search term so the input mounts first
      setTimeout(() => {
        setSearchTerm(e.key);
        searchInputRef.current?.focus();
      }, 0);
    }
  };

  return (
    <div className={className}>
      {label && (
        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
          {label} {required && <span className="text-red-500">*</span>}
        </label>
      )}
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          onClick={toggleOpen}
          onKeyDown={handleButtonKeyDown}
          disabled={disabled || loading}
          className={`w-full px-4 py-3.5 border rounded-xl text-base sm:text-sm text-left bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary/20 focus:border-primary disabled:bg-gray-100 dark:disabled:bg-gray-800 disabled:cursor-not-allowed transition-all duration-200 flex items-center justify-between ${
            error ? 'border-red-300 dark:border-red-500' : 'border-gray-300 dark:border-gray-600'
          } ${!selectedOption ? 'text-gray-400 dark:text-gray-500' : ''}`}
        >
          <span className="truncate">{displayText}</span>
          {loading ? <OathSpinner size="sm" /> : (
            <ChevronDownIcon className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
          )}
        </button>

        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-40"
              onClick={() => setIsOpen(false)}
            />
            <div
              className="bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-lg z-50 overflow-hidden flex flex-col"
              style={dropdownStyle}
            >
              {(options.length > 5 || searchTerm) && (
                <div className="p-2 border-b border-gray-200 dark:border-gray-600">
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        if (filteredOptions.length > 0) {
                          handleSelect(filteredOptions[0].value);
                        }
                      } else if (e.key === 'Escape') {
                        setIsOpen(false);
                        buttonRef.current?.focus();
                      }
                    }}
                    placeholder="Type to search..."
                    className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              <div className="overflow-auto" style={{ maxHeight: 'inherit' }}>
                {!searchTerm && placeholder && (
                  <button
                    type="button"
                    onClick={() => handleSelect('')}
                    className={`w-full text-left px-4 py-3 text-base sm:text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      !value
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-dark dark:text-primary-light font-medium'
                        : 'text-gray-500 dark:text-gray-400'
                    }`}
                  >
                    {placeholder}
                  </button>
                )}
                {filteredOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => handleSelect(opt.value)}
                    className={`w-full text-left px-4 py-3 text-base sm:text-sm hover:bg-gray-50 dark:hover:bg-gray-600 ${
                      opt.value === value
                        ? 'bg-primary-50 dark:bg-primary-900/30 text-primary-dark dark:text-primary-light font-medium'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
                {searchTerm && filteredOptions.length === 0 && (
                  <div className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400">No results found</div>
                )}
              </div>
            </div>
          </>
        )}
      </div>
      {error && <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">{error}</p>}
    </div>
  );
};

/**
 * Search input
 * Usage: <OathSearchInput value={val} onChange={fn} placeholder="Search..." />
 */
export const OathSearchInput = ({
  value,
  onChange,
  placeholder = 'Search...',
  className = ''
}) => (
  <div className={`relative max-w-xs ${className}`}>
    <svg
      className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
    <input
      type="text"
      placeholder={placeholder}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full pl-10 pr-4 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 dark:text-white focus:ring-2 focus:ring-primary focus:border-primary transition-colors placeholder:text-gray-400 dark:placeholder:text-gray-500"
    />
  </div>
);

// =============================================================================
// FEEDBACK COMPONENTS
// =============================================================================

/**
 * Loading spinner (simple)
 * Usage: <OathSpinner size="md" />
 */
export const OathSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = { sm: 'h-4 w-4', md: 'h-5 w-5', lg: 'h-6 w-6' };
  return (
    <svg className={`animate-spin ${sizeClasses[size]} ${className}`} fill="none" viewBox="0 0 24 24">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
    </svg>
  );
};

/**
 * Dual ring spinner with brand colors (teal + orange)
 * Usage: <OathDualSpinner size="md" />
 */
export const OathDualSpinner = ({ size = 'md', className = '' }) => {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-14 h-14',
  };

  const borderSizes = {
    sm: 'border-2',
    md: 'border-4',
    lg: 'border-4',
  };

  const innerInsets = {
    sm: 'inset-0.5',
    md: 'inset-1',
    lg: 'inset-1.5',
  };

  return (
    <div className={`relative ${sizeClasses[size]} ${className}`}>
      <div
        className={`absolute inset-0 rounded-full ${borderSizes[size]} border-transparent border-t-primary animate-spin`}
      />
      <div
        className={`absolute ${innerInsets[size]} rounded-full ${borderSizes[size]} border-transparent border-b-orange-400 animate-spin`}
        style={{ animationDirection: 'reverse', animationDuration: '0.6s' }}
      />
    </div>
  );
};

/**
 * Full page loading state with dual spinner
 * Usage: <OathLoadingPage message="Loading..." />
 */
export const OathLoadingPage = ({ message = 'Loading...' }) => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-primary-50 to-primary-100 dark:from-gray-900 dark:to-gray-950">
    <div className="text-center">
      <OathDualSpinner size="lg" className="mx-auto" />
      <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">{message}</p>
    </div>
  </div>
);

/**
 * Alert/Banner component
 * Usage: <OathAlert variant="info" message="Information message" />
 */
export const OathAlert = ({
  variant = 'info', // 'info' | 'success' | 'warning' | 'error'
  message,
  children,
  className = ''
}) => {
  const variantClasses = {
    info: 'bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800 text-blue-700 dark:text-blue-300',
    success: 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-800 text-green-700 dark:text-green-300',
    warning: 'bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800 text-yellow-700 dark:text-yellow-300',
    error: 'bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800 text-red-700 dark:text-red-300',
    primary: 'bg-primary-50 dark:bg-primary-900/30 border-primary-100 dark:border-primary-800 text-primary-700 dark:text-primary-300',
  };

  return (
    <div className={`p-3 rounded-lg border ${variantClasses[variant]} ${className}`}>
      {message && <p className="text-sm">{message}</p>}
      {children}
    </div>
  );
};

/**
 * Info banner with optional action button
 * Usage: <OathBanner message="Create a new request" actionLabel="New Request" onAction={fn} />
 */
export const OathBanner = ({
  message,
  actionLabel,
  onAction,
  actionIcon: ActionIcon,
  className = ''
}) => (
  <div className={`mb-6 p-4 bg-primary-50 dark:bg-primary-900/30 rounded-lg border border-primary-100 dark:border-primary-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 ${className}`}>
    <p className="text-sm text-primary-700 dark:text-primary-300">{message}</p>
    {actionLabel && onAction && (
      <OathButton onClick={onAction} icon={ActionIcon}>
        {actionLabel}
      </OathButton>
    )}
  </div>
);

/**
 * Empty state display
 * Usage: <OathEmptyState icon={Icon} title="No items" message="Create your first item" />
 */
export const OathEmptyState = ({
  icon: Icon,
  title,
  message,
  action,
  className = ''
}) => (
  <div className={`text-center py-12 ${className}`}>
    {Icon && (
      <div className="mx-auto w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
        <Icon className="h-8 w-8 text-gray-400" />
      </div>
    )}
    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{title}</h3>
    {message && <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{message}</p>}
    {action}
  </div>
);

// =============================================================================
// STATUS COMPONENTS
// =============================================================================

/**
 * Status badge
 * Usage: <OathStatusBadge status="Open" />
 */
export const OathStatusBadge = ({ status, className = '' }) => {
  const statusColors = {
    'Open': 'bg-blue-100 text-blue-800',
    'Quoted': 'bg-yellow-100 text-yellow-800',
    'Accepted': 'bg-green-100 text-green-800',
    'Posted': 'bg-green-100 text-green-800',
    'Verification': 'bg-purple-100 text-purple-800',
    'Cancelled': 'bg-red-100 text-red-800',
    'Closed': 'bg-gray-100 text-gray-800',
    'Pending': 'bg-orange-100 text-orange-800',
    'Declined': 'bg-red-100 text-red-800',
    'Completed': 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
    'Pending Review': 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400',
    'Failed': 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
  };

  const colorClass = statusColors[status] || 'bg-gray-100 text-gray-800';

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colorClass} ${className}`}>
      {status}
    </span>
  );
};

// =============================================================================
// DATA DISPLAY COMPONENTS
// =============================================================================

/**
 * Detail row for displaying label/value pairs
 * Usage: <OathDetailRow label="Name" value="John Doe" />
 */
export const OathDetailRow = ({ label, value, className = '' }) => (
  <div className={`py-2 sm:grid sm:grid-cols-3 sm:gap-4 ${className}`}>
    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">{label}</dt>
    <dd className="mt-1 text-sm text-gray-900 dark:text-white sm:mt-0 sm:col-span-2">{value || '-'}</dd>
  </div>
);

/**
 * Detail section with multiple rows
 * Usage: <OathDetailSection title="Details" items={[{label, value}]} />
 */
export const OathDetailSection = ({ title, items = [], className = '' }) => (
  <div className={className}>
    {title && (
      <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3">{title}</h3>
    )}
    <dl className="divide-y divide-gray-200 dark:divide-gray-700">
      {items.map((item, index) => (
        <OathDetailRow key={index} label={item.label} value={item.value} />
      ))}
    </dl>
  </div>
);

// =============================================================================
// MODAL COMPONENTS
// =============================================================================

/**
 * Modal wrapper
 * Usage: <OathModal isOpen={open} onClose={fn} title="Modal Title">...content...</OathModal>
 * @param {boolean} allowOverflow - If true, disables overflow restrictions (useful for dropdowns)
 */
export const OathModal = ({
  isOpen,
  onClose,
  title,
  children,
  footer,
  size = 'md', // 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  className = '',
  allowOverflow = false,
  persistent = false,
  // When false, the body uses overflow-hidden instead of overflow-y-auto.
  // Use this when the children manage their own scrolling (e.g. an inner table that fills the modal).
  bodyScroll = true
}) => {
  const [showCloseConfirm, setShowCloseConfirm] = useState(false);

  // When not persistent, allow Escape key to close
  useEffect(() => {
    if (!isOpen || persistent) return;
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, persistent, onClose]);

  // Reset confirm state when modal closes
  useEffect(() => {
    if (!isOpen) setShowCloseConfirm(false);
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-[calc(100vw-2rem)] sm:max-w-sm',
    md: 'max-w-[calc(100vw-2rem)] sm:max-w-lg',
    lg: 'max-w-[calc(100vw-2rem)] sm:max-w-2xl',
    xl: 'max-w-[calc(100vw-2rem)] sm:max-w-4xl',
    '2xl': 'max-w-[calc(100vw-2rem)] sm:max-w-6xl',
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70" onClick={persistent ? undefined : () => setShowCloseConfirm(true)} />

        <div className={`relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${sizeClasses[size]} ${allowOverflow ? 'overflow-visible' : 'max-h-[90vh]'} flex flex-col ${className}`}>
          {/* Header - flex-shrink-0 keeps it pinned at top */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 sm:py-4 flex items-center justify-between rounded-t-lg">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h2>
            {!persistent && (
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <svg className="h-5 w-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            )}
          </div>

          {/* Content - flex-1 + min-h-0 allows it to shrink and scroll */}
          <div className={`flex-1 min-h-0 p-4 sm:p-6 ${allowOverflow ? 'overflow-visible' : (bodyScroll ? 'overflow-y-auto' : 'overflow-hidden')}`}>
            {children}
          </div>

          {/* Footer - flex-shrink-0 keeps it pinned at bottom */}
          {footer && (
            <div className="flex-shrink-0 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 px-4 py-3 sm:px-6 sm:py-4 flex gap-3 rounded-b-lg">
              {footer}
            </div>
          )}
        </div>
      </div>

      <OathConfirmDialog
        open={showCloseConfirm}
        title="Close this window?"
        message="Any unsaved changes will be lost."
        confirmLabel="Close"
        cancelLabel="Go Back"
        variant="warning"
        onConfirm={() => { setShowCloseConfirm(false); onClose(); }}
        onCancel={() => setShowCloseConfirm(false)}
      />
    </div>
  );
};

/**
 * Stylized confirmation dialog — drop-in replacement for window.confirm()
 * Usage: <OathConfirmDialog open={...} title="..." message="..." onConfirm={...} onCancel={...} />
 */
export const OathConfirmDialog = ({
  open,
  title = 'Are you sure?',
  message,
  confirmLabel = 'Continue',
  cancelLabel = 'Cancel',
  variant = 'warning', // 'warning' | 'danger'
  onConfirm,
  onCancel,
}) => {
  if (!open) return null;

  const variantStyles = {
    warning: {
      icon: 'text-amber-500 dark:text-amber-400',
      iconBg: 'bg-amber-50 dark:bg-amber-900/20',
      button: 'bg-amber-500 hover:bg-amber-600 focus:ring-amber-500',
    },
    danger: {
      icon: 'text-red-500 dark:text-red-400',
      iconBg: 'bg-red-50 dark:bg-red-900/20',
      button: 'bg-red-600 hover:bg-red-700 focus:ring-red-500',
    },
  };
  const styles = variantStyles[variant] || variantStyles.warning;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black/50 dark:bg-black/70" onClick={onCancel} />
        <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-sm p-6">
          <div className="flex flex-col items-center text-center">
            <div className={`w-12 h-12 rounded-full ${styles.iconBg} flex items-center justify-center mb-4`}>
              <svg className={`h-6 w-6 ${styles.icon}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
            </div>
            <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-1.5">
              {title}
            </h3>
            {message && (
              <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
                {message}
              </p>
            )}
          </div>
          <div className="flex gap-3 mt-6">
            <button
              type="button"
              onClick={onCancel}
              className="flex-1 px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-300 transition-colors"
            >
              {cancelLabel}
            </button>
            <button
              type="button"
              onClick={onConfirm}
              className={`flex-1 px-4 py-2.5 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 dark:focus:ring-offset-gray-800 transition-colors ${styles.button}`}
            >
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// =============================================================================
// UTILITY EXPORTS
// =============================================================================

/**
 * Common class strings for manual usage
 */
export const oathClasses = {
  // Page backgrounds
  pageBackground: 'bg-primary-50 min-h-full py-6',
  pageContainer: 'max-w-5xl mx-auto px-4 sm:px-6',

  // Cards
  card: 'bg-white rounded-xl shadow-sm border border-primary-100',
  cardPadding: 'p-4 sm:p-6',

  // Text
  pageTitle: 'text-xl sm:text-2xl font-semibold text-primary',
  pageSubtitle: 'text-sm text-gray-500 mt-1',
  sectionTitle: 'text-lg font-semibold text-gray-900',

  // Inputs
  input: 'w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary focus:border-primary',
  inputError: 'border-red-300',
  inputDisabled: 'bg-gray-100 cursor-not-allowed',

  // Buttons
  buttonPrimary: 'inline-flex items-center justify-center px-4 py-2.5 bg-primary hover:bg-primary-dark text-white text-sm font-medium rounded-lg shadow-sm transition-colors',
  buttonSecondary: 'inline-flex items-center justify-center px-4 py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-medium rounded-lg transition-colors',
  buttonOutline: 'inline-flex items-center justify-center px-4 py-2.5 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors',

  // Status badges
  statusOpen: 'bg-blue-100 text-blue-800',
  statusQuoted: 'bg-yellow-100 text-yellow-800',
  statusAccepted: 'bg-green-100 text-green-800',
  statusCancelled: 'bg-red-100 text-red-800',
};

const OathUI = {
  OathPageWrapper,
  OathHeaderCard,
  OathContentCard,
  OathSection,
  OathSectionCard,
  OathDetailField,
  OathAgencySelector,
  OathAgencyInfo,
  OathButton,
  OathInput,
  OathSelect,
  OathSearchInput,
  OathSpinner,
  OathDualSpinner,
  OathLoadingPage,
  OathAlert,
  OathBanner,
  OathEmptyState,
  OathStatusBadge,
  OathDetailRow,
  OathDetailSection,
  OathModal,
  oathClasses,
};

export default OathUI;
